import React, { useState } from 'react';
import { QRScanner } from './QRScanner';
import { useLanguage } from '../contexts/LanguageContext';

interface ValidationResult {
  success: boolean;
  message?: string;
  error?: string;
  coupon?: {
    id: number;
    title: string;
    description: string;
    discount: string;
    vendor_name: string;
    driver_name: string;
  };
  redeemed_at?: string;
}

export const MobileScannerPage: React.FC = () => {
  const { t } = useLanguage();
  const [isScanning, setIsScanning] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const handleScanSuccess = async (token: string) => {
    setIsScanning(false);
    
    // Add haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
    
    try {
      const response = await fetch('https://cf.qrsp.app/api/qr/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      setValidationResult(data);

      // Different vibration patterns for success/failure
      if (navigator.vibrate) {
        if (data.success) {
          navigator.vibrate([200, 100, 200]); // Success pattern
        } else {
          navigator.vibrate([500]); // Error pattern
        }
      }

      // Auto-hide result after 5 seconds if successful
      if (data.success) {
        setTimeout(() => {
          setValidationResult(null);
        }, 5000);
      }

    } catch (error) {
      setValidationResult({
        success: false,
        error: t('scanner.connection.error'),
        message: t('scanner.connection.error.desc')
      });
    }
  };

  const handleScanError = (error: string) => {
    setValidationResult({
      success: false,
      error: t('scanner.scan.error'),
      message: error
    });
  };

  const startScanning = () => {
    setIsScanning(true);
    setValidationResult(null);
  };

  const stopScanning = () => {
    setIsScanning(false);
  };

  const clearResult = () => {
    setValidationResult(null);
    startScanning();
  };

  return (
    <div className="h-full flex flex-col bg-black">
      {/* Header */}
      <div className="bg-black text-white p-4 safe-area-top">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{t('scanner.title')}</h1>
            <p className="text-gray-300 text-sm">{t('scanner.subtitle')}</p>
          </div>
          <div className="text-right">
            <div className={`text-xs px-2 py-1 rounded ${isScanning ? 'bg-green-600' : 'bg-gray-600'}`}>
              {isScanning ? t('scanner.status.active') : t('scanner.status.stopped')}
            </div>
          </div>
        </div>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 relative">
        {isScanning ? (
          <QRScanner
            onScanSuccess={handleScanSuccess}
            onScanError={handleScanError}
            isActive={isScanning}
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-gray-900">
            <div className="text-center text-white p-8">
              <div className="text-6xl mb-4">📷</div>
              <h2 className="text-xl font-semibold mb-2">{t('scanner.camera.stopped')}</h2>
              <p className="text-gray-300 text-sm mb-6">
                {t('scanner.camera.stopped.desc')}
              </p>
            </div>
          </div>
        )}

        {/* Overlay Instructions */}
        {isScanning && (
          <div className="absolute top-4 left-4 right-4">
            <div className="bg-black bg-opacity-70 text-white p-3 rounded-lg">
              <p className="text-sm text-center">
                {t('scanner.camera.instruction')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-black p-4">
        <div className="space-y-3">
          {/* Main Action Button */}
          <button
            onClick={isScanning ? stopScanning : startScanning}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              isScanning
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isScanning ? t('scanner.stop') : t('scanner.start')}
          </button>
        </div>
      </div>

      {/* Validation Result Modal */}
      {validationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-md rounded-2xl p-6 ${
            validationResult.success ? 'bg-green-600' : 'bg-red-600'
          } text-white`}>
            <div className="text-center">
              <div className="text-6xl mb-4">
                {validationResult.success ? '✅' : '❌'}
              </div>
              
              <h3 className="text-2xl font-bold mb-4">
                {validationResult.success ? t('scanner.valid') : t('scanner.invalid')}
              </h3>
              
              {validationResult.success && validationResult.coupon && (
                <div className="bg-white bg-opacity-20 rounded-xl p-4 mb-4">
                  <h4 className="font-bold text-lg">{validationResult.coupon.title}</h4>
                  <p className="text-sm opacity-90">{validationResult.coupon.vendor_name}</p>
                  <p className="text-2xl font-bold mt-2">{validationResult.coupon.discount}</p>
                  {validationResult.coupon.description && (
                    <p className="text-xs mt-2 opacity-75">{validationResult.coupon.description}</p>
                  )}
                  <div className="text-xs mt-2 opacity-75">
                    Driver: {validationResult.coupon.driver_name}
                  </div>
                </div>
              )}
              
              <p className="mb-6 opacity-90">
                {validationResult.message || validationResult.error}
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={clearResult}
                  className="w-full bg-white text-gray-900 py-3 rounded-xl font-bold"
                >
                  {validationResult.success ? t('scanner.next') : t('scanner.retry')}
                </button>
                
                <button
                  onClick={() => setValidationResult(null)}
                  className="w-full bg-transparent border-2 border-white text-white py-3 rounded-xl font-bold"
                >
                  {t('scanner.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};